import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput, { formatINR } from '../../components/SliderInput';

const SIPCalculator = () => {
  const [monthly, setMonthly] = useState(10000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);

  const { invested, value, gains } = useMemo(() => {
    const n = years * 12;
    const i = rate / 100 / 12;
    // Future value of monthly SIP
    const fv = i === 0 ? monthly * n : monthly * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
    const inv = monthly * n;
    return { invested: inv, value: fv, gains: fv - inv };
  }, [monthly, years, rate]);

  const gainsPct = value > 0 ? (gains / value) * 100 : 0;

  return (
    <CalculatorLayout
      title="SIP Calculator"
      subtitle="Project the future value of your monthly Systematic Investment Plan in mutual funds."
      category="Financial"
      description={<>
        <p>A Systematic Investment Plan (SIP) lets you invest a fixed amount in mutual funds every month. The power of compounding can grow modest monthly contributions into a substantial corpus over time.</p>
        <p>Use this calculator to estimate the future value of your investment based on monthly contribution, expected annual return, and tenure.</p>
      </>}
      formulaNote="FV = P × [((1 + i)ⁿ − 1) / i] × (1 + i)    where P = monthly SIP, i = monthly rate, n = months"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <SliderInput label="Monthly Investment" unit="₹" unitColor="saffron" value={monthly} onChange={setMonthly} min={500} max={200000} step={500} format={formatINR} />
          <SliderInput label="Expected Return (p.a.)" unit="%" unitColor="green" value={rate} onChange={setRate} min={1} max={30} step={0.5} />
          <SliderInput label="Time Period" unit="yr" value={years} onChange={setYears} min={1} max={40} step={1} />
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Future Value</div>
          <div className="font-display text-3xl md:text-4xl font-bold text-bharat-navy display-font mt-1">{formatINR(value)}</div>

          <div className="grid grid-cols-2 gap-3 mt-5">
            <Stat label="Total Invested" value={formatINR(invested)} />
            <Stat label="Estimated Gains" value={formatINR(gains)} tone="green" />
          </div>

          <div className="mt-5">
            <div className="text-xs text-slate-500 mb-2 flex justify-between">
              <span>Invested <span className="text-bharat-navy font-semibold">{(100 - gainsPct).toFixed(1)}%</span></span>
              <span>Gains <span className="text-indgreen-700 font-semibold">{gainsPct.toFixed(1)}%</span></span>
            </div>
            <div className="h-3 rounded-full bg-slate-200 overflow-hidden flex">
              <div style={{ width: `${100 - gainsPct}%` }} className="bg-saffron-500" />
              <div style={{ width: `${gainsPct}%` }} className="bg-indgreen-500" />
            </div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Stat = ({ label, value, tone }) => {
  const color = tone === 'green' ? 'text-indgreen-700' : tone === 'saffron' ? 'text-saffron-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-3 border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font font-semibold mt-0.5 ${color}`}>{value}</div>
    </div>
  );
};

export default SIPCalculator;
